/**
 * Deals Service
 * Handles all deal-related data operations with workspace-first RLS
 */

import { supabase } from '@/integrations/supabase/client';
import type { Database, Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';
import { logger } from '@/lib/logger';
import { getSupabaseErrorMessage } from '@/lib/errorHandler';
import { WorkspaceScope, applyWorkspaceScope } from '@/lib/workspaceScope';

export type Deal = Tables<'deals'>;

export interface CreateDealInput extends Omit<TablesInsert<'deals'>, 'workspace_id'> {
  workspace_id: string;
}

export interface UpdateDealInput extends Partial<TablesUpdate<'deals'>> {
  id: string;
  workspace_id: string;
}

export class DealsService {
  /**
   * Get all deals for a workspace scope
   * Fetches deals and leads separately to avoid join issues
   */
  static async getDeals(scope: WorkspaceScope, filters?: {
    stage?: string;
    expectedCloseDateFrom?: string;
    expectedCloseDateTo?: string;
  }): Promise<Deal[]> {
    logger.info('[DealsService.getDeals] Starting', { scope, filters });
    
    let query = supabase
      .from('deals')
      .select('*');

    // Apply workspace scope filter
    query = applyWorkspaceScope(query, scope);

    if (filters?.stage) {
      query = query.eq('stage', filters.stage);
    }

    if (filters?.expectedCloseDateFrom) {
      query = query.gte('expected_close_date', filters.expectedCloseDateFrom);
    }

    if (filters?.expectedCloseDateTo) {
      query = query.lte('expected_close_date', filters.expectedCloseDateTo);
    }

    query = query.order('updated_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      logger.error('[DealsService] Error fetching deals', error, { scope, filters, errorCode: error.code, errorMessage: error.message });
      
      // Handle RLS/permission errors gracefully - return empty array instead of throwing
      // This prevents 500 errors in UI when switching workspaces
      if (error.code === '42501' || error.code === 'PGRST301' || 
          error.message?.includes('permission') || error.message?.includes('RLS') ||
          error.message?.includes('row-level security')) {
        logger.warn('[DealsService] Permission denied, returning empty array', { scope, filters });
        return [];
      }
      
      throw new Error(getSupabaseErrorMessage(error));
    }

    logger.info('[DealsService.getDeals] Success', { count: data?.length || 0, scope });
    return (data || []) as Deal[];
  }

  /**
   * Get single deal by ID (with workspace scope check)
   */
  static async getDeal(dealId: string, scope: WorkspaceScope): Promise<Deal | null> {
    let query = supabase
      .from('deals')
      .select('*')
      .eq('id', dealId);
    
    // Apply workspace scope filter
    query = applyWorkspaceScope(query, scope);
    
    const { data, error } = await query.maybeSingle();

    if (error) {
      logger.error('[DealsService] Error fetching deal', error, { dealId, scope });
      throw new Error(getSupabaseErrorMessage(error));
    }

    return data as Deal | null;
  }

  /**
   * Create a deal
   */
  static async createDeal(input: CreateDealInput): Promise<Deal> {
    logger.info('[DealsService.createDeal] Starting', { workspaceId: input.workspace_id, leadId: input.lead_id, stage: input.stage });
    
    const { data, error } = await supabase
      .from('deals')
      .insert(input as any)
      .select()
      .single();

    if (error) {
      logger.error('[DealsService] Error creating deal', error, { 
        workspaceId: input.workspace_id, 
        errorCode: error.code, 
        errorMessage: error.message,
        errorDetails: error.details,
        errorHint: error.hint
      });
      throw new Error(getSupabaseErrorMessage(error));
    }

    logger.info('[DealsService.createDeal] Success', { dealId: data?.id, workspaceId: input.workspace_id });
    return data as Deal;
  }

  /**
   * Update a deal
   */
  static async updateDeal(input: UpdateDealInput): Promise<Deal> {
    const { id, workspace_id, ...updates } = input;

    logger.info('[DealsService.updateDeal] Starting', { 
      dealId: id, 
      workspaceId: workspace_id,
      updates: Object.keys(updates)
    });

    const { data, error } = await supabase
      .from('deals')
      .update(updates as any)
      .eq('id', id)
      .eq('workspace_id', workspace_id)
      .select()
      .single();

    if (error) {
      logger.error('[DealsService] Error updating deal', error, { 
        dealId: input.id, 
        workspaceId: input.workspace_id,
        errorCode: error.code,
        errorMessage: error.message,
        errorDetails: error.details,
        errorHint: error.hint,
        updates: Object.keys(updates)
      });
      throw new Error(getSupabaseErrorMessage(error));
    }

    logger.info('[DealsService.updateDeal] Success', { dealId: id, workspaceId: workspace_id });
    return data as Deal;
  }

  /**
   * Delete a deal
   */
  static async deleteDeal(dealId: string, workspaceId: string): Promise<void> {
    const { error } = await supabase
      .from('deals')
      .delete()
      .eq('id', dealId)
      .eq('workspace_id', workspaceId);

    if (error) {
      logger.error('[DealsService] Error deleting deal', error, { dealId, workspaceId });
      throw new Error(getSupabaseErrorMessage(error));
    }
  }
}

